import { Button } from "primereact/button";
import { DataView } from "primereact/dataview";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ToastContext } from "src/core/context/ToastContext";
import { useErrorHandler } from "src/core/error/useErrorHandler";
import { useError } from "src/core/hooks/useError";
import { logService } from "src/core/services/logService";
import type { ClinicDoctorModel } from "src/features/clinics/models/clinicDoctorModel";
import { useClinicsStore } from "src/features/clinics/services/useClinicsStore";
import { useFavouriteStore } from "../../services/useFavouriteStore";
import "./FavouriteDoctorSelect.css";

export type FavouriteDoctorSelectProps = {
  visible: boolean;
  onHide: () => void;
};

export function FavouriteDoctorSelect({
  visible,
  onHide,
}: FavouriteDoctorSelectProps) {
  const toastRef = useContext(ToastContext);
  const searchNameRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();
  const { getErrorMessage } = useError();

  const clinicsStore = useClinicsStore();
  const favouriteStore = useFavouriteStore();
  const errorHandler = useErrorHandler();

  const [selectableClinicDoctors, setSelectableClinicDoctors] =
    useState<ClinicDoctorModel[]>();

  const [searchName, setSearchName] = useState("");
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const searchNameFocus = () => {
    setTimeout(() => {
      searchNameRef.current?.focus();
    }, 100);
  };

  const onSearchNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearchName(value);
    }, 500);
  };

  const onClinicDoctorSelect = async (clinicDoctor: ClinicDoctorModel) => {
    if (!clinicDoctor.doctorId || clinicDoctor.doctorId === 0) return;

    await favouriteStore
      .favouritePost(clinicDoctor)
      .then(() => {
        logService.log(
          "FavouriteDoctorSelect",
          `ClinicDoctor with ID ${clinicDoctor.doctorId} added to favourites.`
        );
        toastRef?.current?.show({
          severity: "success",
          summary: t("Home.FavouriteDoctors.AddTitle"),
          detail: t("Home.FavouriteDoctors.AddSuccess"),
          life: 5000,
        });
        onHide();
      })
      .catch((error) => {
        errorHandler.setError({
          title: t("Home.FavouriteDoctors.AddTitle"),
          message:
            t("Home.FavouriteDoctors.AddFailed") + " " + getErrorMessage(error),
        });
      });

    await favouriteStore.favouriteList();
  };

  useEffect(() => {
    if (visible) {
      searchNameFocus();
    }
  }, [visible]);

  useEffect(() => {
    const clinicDoctors = clinicsStore.clinicDoctors;
    const favourites = favouriteStore.favourites;

    const selectableClinicDoctors = clinicDoctors
      .filter((clinicDoctor) => {
        return (
          !!clinicDoctor.doctorName &&
          !favourites.some(
            (favourite) =>
              favourite.doctorId === clinicDoctor.doctorId &&
              favourite.clinicId === clinicDoctor.clinicId
          ) &&
          (searchName.length === 0 ||
            clinicDoctor.doctorName
              ?.toLowerCase()
              .includes(searchName.toLowerCase()) ||
            clinicDoctor.clinicName
              ?.toLowerCase()
              .includes(searchName.toLowerCase()))
        );
      })
      .sort((a, b) => {
        return a.doctorName.localeCompare(b.doctorName);
      });

    if (logService.globalLog)
      logService.log("selectableClinicDoctors", selectableClinicDoctors);

    setSelectableClinicDoctors(selectableClinicDoctors);
  }, [clinicsStore.clinicDoctors, favouriteStore.favourites, searchName]);

  return (
    <div className="favouriteDoctorSelect">
      <Dialog
        header={t("Home.FavouriteDoctors.AddTitle")}
        modal={true}
        breakpoints={{
          "1200px": "50vw",
          "992px": "75vw",
          "768px": "90vw",
          "576px": "95vw",
        }}
        style={{
          minHeight: "50vh",
          maxHeight: "70vh",
          minWidth: "200px",
          maxWidth: "500px",
          height: "40vw",
          width: "40vw",
        }}
        draggable={false}
        resizable={false}
        maximizable={false}
        closeOnEscape={false}
        visible={visible}
        onHide={onHide}
      >
        <DataView
          value={selectableClinicDoctors}
          header={
            <div className="flex">
              <div className="flex flex-grow-1 justify-content-start align-items-center gap-2">
                <div className="font-bold">{t("Base.Search")}</div>

                <div
                  className="p-inputgroup align-items-center"
                  style={{ width: "50%" }}
                >
                  <InputText
                    ref={searchNameRef}
                    id="searchName"
                    className="w100"
                    defaultValue={searchName}
                    onChange={onSearchNameChange}
                    type="text"
                  />
                  <Button
                    icon="pi pi-times"
                    severity="warning"
                    onClick={() => {
                      if (searchNameRef.current)
                        searchNameRef.current.value = "";
                      setSearchName("");
                      searchNameFocus();
                    }}
                  ></Button>
                </div>
              </div>
            </div>
          }
          listTemplate={(items?: ClinicDoctorModel[]) => {
            if (!items || items.length === 0) return null;

            let list = items.map((item, index) => {
              return (
                <div
                  key={index}
                  className={
                    "col-12 " + (index > 0 ? "border-top-1 surface-border" : "")
                  }
                >
                  <div className="flex flex-column sm:flex-row sm:align-items-center gap-3">
                    <div className="flex flex-column flex-grow-1">
                      <div className="font-bold">{item.doctorName}</div>
                      <div className="">{item.clinicName}</div>
                      <div className="font-italic">{item.clinicAddress}</div>
                    </div>

                    <div>
                      <Button
                        icon="pi pi-check"
                        severity="success"
                        onClick={() => {
                          onClinicDoctorSelect(item);
                        }}
                      ></Button>
                    </div>
                  </div>
                </div>
              );
            });

            return <>{list}</>;
          }}
        />
      </Dialog>
    </div>
  );
}
